import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import getCookie from '../utils/getCookie';
import { API_CONFIG } from '../apiConfig';
import styles from '../css/AvisoManagement.module.css';

const LUGAR_POR_DEFECTO = 'Iglesia';

// 1. Recibimos user y onLogout como props del padre (PanelControl)
const AvisoManagement = ({ user, onLogout }) => {
    const [dias, setDias] = useState([]);
    const [selectedDiaId, setSelectedDiaId] = useState('');
    const [titulo, setTitulo] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [mensaje, setMensaje] = useState({ text: '', type: '' });
    const [editingAvisoId, setEditingAvisoId] = useState(null);

    // --- CARGA DE DATOS ---
    const fetchSemana = useCallback(async () => {
        try {
            const response = await axios.get(`${API_CONFIG.AVISOS_SEMANA}?lugar=${LUGAR_POR_DEFECTO}`);
            setDias(response.data.dias || []);
            if (response.data.dias?.length > 0 && !selectedDiaId) {
                setSelectedDiaId(response.data.dias[0].id);
            }
        } catch (error) {
            setMensaje({ text: 'Error al conectar con el servidor.', type: 'error' });
        }
    }, [selectedDiaId]);

    useEffect(() => { 
        fetchSemana(); 
    }, [fetchSemana]);

    const cancelarEdicion = () => {
        setEditingAvisoId(null);
        setTitulo('');
        setDescripcion('');
    };

    // --- GUARDADO (POST / PUT) ---
    const handleGuardarAviso = async (e) => {
        e.preventDefault();
        const csrftoken = getCookie('csrftoken');
        const payload = { dia: selectedDiaId, titulo, descripcion };

        try {
            if (editingAvisoId) {
                await axios.put(API_CONFIG.AVISOS_EDITAR(editingAvisoId), payload, {
                    headers: { 'X-CSRFToken': csrftoken },
                    withCredentials: true
                });
                setMensaje({ text: 'Aviso actualizado correctamente.', type: 'success' });
            } else {
                await axios.post(API_CONFIG.AVISOS_CREAR, payload, {
                    headers: { 'X-CSRFToken': csrftoken },
                    withCredentials: true
                });
                setMensaje({ text: 'Aviso creado con éxito.', type: 'success' });
            }
            cancelarEdicion();
            fetchSemana();
            setTimeout(() => setMensaje({ text: '', type: '' }), 3000);
        } catch (error) {
            setMensaje({ text: 'Error de sesión o red. Intente reingresar.', type: 'error' });
        }
    };

    // --- ELIMINACIÓN ---
    const handleEliminarAviso = async (id) => {
        if (!window.confirm('¿Desea eliminar este aviso permanentemente?')) return;
        const csrftoken = getCookie('csrftoken');
        try {
            await axios.delete(API_CONFIG.AVISOS_ELIMINAR(id), {
                headers: { 'X-CSRFToken': csrftoken },
                withCredentials: true
            });
            setMensaje({ text: 'Aviso eliminado.', type: 'success' });
            fetchSemana();
            setTimeout(() => setMensaje({ text: '', type: '' }), 3000);
        } catch (error) { 
            setMensaje({ text: 'No se pudo eliminar el aviso.', type: 'error' });
        }
    };

    const diaActual = dias.find(d => String(d.id) === String(selectedDiaId));

    return (
        <div className={styles.container}>
            {/* --- NAVBAR DE ADMINISTRACIÓN --- */}
            <nav className={styles.adminNavbar}>
                <div className={styles.navLeft}>
                    <img src="/Logo-Iump-Concon.ico" alt="Logo" className={styles.navLogo} />
                    <div className={styles.brandText}>
                        <span className={styles.churchName}>IUMP Concón</span>
                        <span className={styles.panelTag}>Avisos</span>
                    </div>
                </div>

                <div className={styles.navRight}>
                    <div className={styles.userInfo}>
                        <span className={styles.userRole}>Administrador</span>
                        <span className={styles.userName}>{user?.username || 'Pastor'}</span>
                    </div>
                    <button onClick={onLogout} className={styles.logoutBtn} title="Cerrar Sesión">
                        <i className="fas fa-power-off"></i>
                    </button>
                </div>
            </nav>

            <div className={styles.adminContent}>
                <h3 className={styles.title}>
                    <i className="fas fa-cog"></i> Gestión de Avisos Pastoral
                </h3>
                <br /><br /><br />

                {mensaje.text && (
                    <div className={`${styles.alert} ${mensaje.type === 'error' ? styles.error : styles.success}`}>
                        {mensaje.text}
                    </div>
                )}
                
                <div className={`${styles.card} ${styles.selectorSection}`}>
                    <label className={styles.label}>Jornada:</label>
                    <select 
                        className={styles.selectInput} 
                        value={selectedDiaId} 
                        onChange={(e) => setSelectedDiaId(e.target.value)}
                    >
                        {dias.map(dia => (
                            <option key={dia.id} value={dia.id}>{dia.dia_semana_display}</option>
                        ))}
                    </select>
                </div>

                <form 
                    onSubmit={handleGuardarAviso} 
                    className={`${styles.card} ${styles.formCard} ${editingAvisoId ? styles.editingActive : ''}`}
                >
                    <h4 className={styles.formTitle}>{editingAvisoId ? 'Modificar Registro' : 'Nuevo Aviso'}</h4>
                    <div className={styles.inputGroup}>
                        <input 
                            className={styles.inputField}
                            type="text" 
                            placeholder="Título (Ej: Reunión de Jóvenes)" 
                            value={titulo} 
                            onChange={(e) => setTitulo(e.target.value)} 
                            required 
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <textarea 
                            className={styles.textField}
                            placeholder="Escriba el detalle del aviso..." 
                            value={descripcion} 
                            onChange={(e) => setDescripcion(e.target.value)} 
                            required 
                        />
                    </div>
                    <div className={styles.actions}>
                        <button type="submit" className={styles.saveBtn}>
                            {editingAvisoId ? 'Actualizar' : 'Publicar'}
                        </button>
                        
                        {editingAvisoId && (
                            <button 
                                type="button" 
                                onClick={cancelarEdicion} 
                                className={styles.cancelBtn}
                            >
                                <i className="fas fa-times"></i> Descartar
                            </button>
                        )}
                    </div>
                </form>

                <div className={styles.card}>
                    <h4 className={styles.listTitle}>
                        Avisos en {diaActual?.dia_semana_display}
                    </h4>
                    <ul className={styles.avisoList}>
                        {diaActual?.avisos.map((aviso) => (
                            <li key={aviso.id} className={styles.avisoItem}>
                                <div className={styles.avisoInfo}>
                                    <strong>{aviso.titulo}</strong>
                                    <p>{aviso.descripcion}</p>
                                </div>
                                <div className={styles.itemActions}>
                                    <button onClick={() => {
                                        setEditingAvisoId(aviso.id);
                                        setTitulo(aviso.titulo);
                                        setDescripcion(aviso.descripcion);
                                        window.scrollTo({top: 0, behavior: 'smooth'});
                                    }} className={styles.editBtn}>
                                        <i className="fas fa-pen-nib"></i>
                                    </button>
                                    <button onClick={() => handleEliminarAviso(aviso.id)} className={styles.deleteBtn}>
                                        <i className="fas fa-trash-alt"></i>
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default AvisoManagement;